import { ContentPlansPage } from "~/modules/analytics/pages/content-plans";
/**
 * Every reading, as routes under its stage's path.
 *
 * One table rather than five route lists, and it is a `Record` over the slugs
 * in `LOOP`, so a reading added there with no screen behind it is a compile
 * error rather than a row in the sidebar that lands on "not found".
 *
 * Readings that are several files live in a folder of their own
 * (`pages/insights/`); the rest are one file each beside this one. What decides
 * it is size, not the stage — a stage does not need a folder to be a stage,
 * the table is what says which screens are its.
 */

import type { ReactElement } from "react";
import { Navigate, Route, Routes } from "react-router";
import { useAccess } from "~/core/access/index";
import { useT } from "~/core/i18n";
import { Empty } from "~/core/ui/states";
import type { ReadingProps } from "~/modules/analytics/components/stage";
import { ADVANCED_READINGS, type LoopStage, type ReadingSlug } from "~/modules/analytics/lib/loop";
import { BadgePage } from "~/modules/analytics/pages/badge";
import { BriefsPage } from "~/modules/analytics/pages/briefs";
import { CompetitorsPage } from "~/modules/analytics/pages/competitors";
import { CrawlersPage } from "~/modules/analytics/pages/crawlers";
import { DistributionPage } from "~/modules/analytics/pages/distribution";
import { FactCheckPage } from "~/modules/analytics/pages/factcheck";
import { GapsPage } from "~/modules/analytics/pages/gaps";
import { InsightPersonas, InsightRegions } from "~/modules/analytics/pages/insights/audiences";
import { InsightCitations } from "~/modules/analytics/pages/insights/citations";
import { InsightFanouts } from "~/modules/analytics/pages/insights/fanouts";
import { InsightPlatforms } from "~/modules/analytics/pages/insights/platforms";
import { InsightPrompts } from "~/modules/analytics/pages/insights/prompts";
import { InsightSentiment } from "~/modules/analytics/pages/insights/sentiment";
import { InsightVisibility } from "~/modules/analytics/pages/insights/visibility";
import { OpportunitiesPage } from "~/modules/analytics/pages/opportunities";
import { PagesPage } from "~/modules/analytics/pages/pages";
import { ReflowPagesPage } from "~/modules/analytics/pages/reflow-pages";
import { ReflowsPage } from "~/modules/analytics/pages/reflows";
import { TemplatesPage } from "~/modules/analytics/pages/templates";
import { AttributionPage } from "~/modules/analytics/pages/tracking/attribution";
import { GoalsPage } from "~/modules/analytics/pages/tracking/goals";
import { JourneysPage } from "~/modules/analytics/pages/tracking/journeys";
import { LivePage } from "~/modules/analytics/pages/tracking/live";
import { TrafficPage } from "~/modules/analytics/pages/tracking/traffic";
import { VitalsPage } from "~/modules/analytics/pages/tracking/vitals";

const SCREENS: Record<ReadingSlug, (props: ReadingProps) => ReactElement> = {
	visibility: InsightVisibility,
	prompts: InsightPrompts,
	fanouts: InsightFanouts,
	platforms: InsightPlatforms,
	regions: InsightRegions,
	personas: InsightPersonas,
	sentiment: InsightSentiment,
	citations: InsightCitations,
	competitors: CompetitorsPage,
	gaps: GapsPage,
	factcheck: FactCheckPage,
	opportunities: OpportunitiesPage,
	// One list, two stages: what is waiting to be approved, and what is live.
	// The same screen for both made "generate" and "publish" look like one step,
	// and they are the two the customer is asked to act on differently.
	briefs: BriefsPage,
	drafts: (props) => <PagesPage {...props} stage="draft" />,
	published: (props) => <PagesPage {...props} stage="published" />,
	crawlers: CrawlersPage,
	badge: BadgePage,
	templates: TemplatesPage,
	plans: ContentPlansPage,
	distribution: DistributionPage,
	reflows: ReflowsPage,
	since: ReflowPagesPage,
	traffic: TrafficPage,
	journeys: JourneysPage,
	goals: GoalsPage,
	attribution: AttributionPage,
	vitals: VitalsPage,
	live: LivePage,
};

/**
 * One stage's readings.
 *
 * The stage's own path lands on its first reading — the one the others refine,
 * and in every case the one somebody arriving at the stage wants first.
 */
export function StageRoutes({ stage, ...props }: ReadingProps & { stage: LoopStage }) {
	const t = useT();
	const { advanced } = useAccess();
	const first = stage.readings[0].slug;

	return (
		<Routes>
			<Route index element={<Navigate to={first} replace />} />
			{stage.readings.map((reading) => {
				const Screen = SCREENS[reading.slug];
				// A reading the plan does not include is not a 404: the brand is
				// still there and so is the stage, so the address lands on the
				// stage's first row rather than on nothing.
				return !advanced && ADVANCED_READINGS.has(reading.slug) ? (
					<Route key={reading.slug} path={reading.slug} element={<Navigate to={`../${first}`} replace />} />
				) : (
					<Route key={reading.slug} path={reading.slug} element={<Screen {...props} />} />
				);
			})}
			{/* The filing that used to be a ninth detection screen is on the
			    brand's Settings screen now, so the path somebody kept redirects
			    rather than 404ing. */}
			<Route path="settings" element={<Navigate to={`/w/${props.entityId}/settings`} replace />} />
			<Route path="*" element={<Empty title={t("errors.not_found")} />} />
		</Routes>
	);
}
